import { c as _c } from "react/compiler-runtime";
import type { Dispatch, SetStateAction } from "react";
import { useMemo, useRef, useState } from "react";
import { useClickAway } from "react-use";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import type { ApiCreateGroup } from "@/generated/models/ApiCreateGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { validateGroupPayload } from "@/services/groups/groupMutations";
import { createInitialInlineGroupBuilderState, dedupeInlineIdentities, getInlineGroupDraftSummary, getInlineIdentityAddresses } from "./createWaveInlineGroupBuilder";
import type { CreateWaveInlineGroupBuilderState, CreateWaveInlineGroupPanel, CreateWaveInlineGroupRuleType } from "./createWaveInlineGroupBuilder";
const PANEL_ACTIONS: CreateWaveInlineGroupPanel = "actions";
const PANEL_IDENTITY: CreateWaveInlineGroupPanel = "identity";
const PANEL_RULE_LIST: CreateWaveInlineGroupPanel = "rule-list";
const PANEL_RULE_EDITOR: CreateWaveInlineGroupPanel = "rule-editor";
const PANEL_SEARCH: CreateWaveInlineGroupPanel = "search";
function getDisplayedBuilder({
  builder,
  disabled
}: {
  readonly builder: CreateWaveInlineGroupBuilderState;
  readonly disabled: boolean;
}): CreateWaveInlineGroupBuilderState {
  if (!disabled) {
    return builder;
  }
  return {
    ...builder,
    panel: PANEL_ACTIONS,
    activeRule: null
  };
}
function buildIdentityDraftState({
  current,
  identities
}: {
  readonly current: CreateWaveInlineGroupBuilderState;
  readonly identities: readonly CommunityMemberMinimal[];
}): CreateWaveInlineGroupBuilderState {
  return {
    ...current,
    identities,
    draft: {
      ...current.draft,
      group: {
        ...current.draft.group,
        identity_addresses: getInlineIdentityAddresses(identities)
      }
    }
  };
}
function collapseInlineGroupBuilder(current: CreateWaveInlineGroupBuilderState): CreateWaveInlineGroupBuilderState {
  return {
    ...current,
    panel: PANEL_ACTIONS,
    activeRule: null
  };
}
function openInlineGroupPanel({
  current,
  panel
}: {
  readonly current: CreateWaveInlineGroupBuilderState;
  readonly panel: CreateWaveInlineGroupPanel;
}): CreateWaveInlineGroupBuilderState {
  return {
    ...current,
    panel,
    activeRule: null
  };
}
function openInlineGroupRule({
  current,
  rule
}: {
  readonly current: CreateWaveInlineGroupBuilderState;
  readonly rule: CreateWaveInlineGroupRuleType;
}): CreateWaveInlineGroupBuilderState {
  return {
    ...current,
    activeRule: rule,
    panel: PANEL_RULE_EDITOR
  };
}
function toggleInlineGroupRule({
  current,
  rule
}: {
  readonly current: CreateWaveInlineGroupBuilderState;
  readonly rule: CreateWaveInlineGroupRuleType;
}): CreateWaveInlineGroupBuilderState {
  if (current.panel === PANEL_RULE_EDITOR && current.activeRule === rule) {
    return {
      ...current,
      activeRule: null,
      panel: PANEL_RULE_LIST
    };
  }
  return openInlineGroupRule({
    current,
    rule
  });
}
function clearInlineGroupDraft(current: CreateWaveInlineGroupBuilderState): CreateWaveInlineGroupBuilderState {
  const next = createInitialInlineGroupBuilderState();
  return {
    ...next,
    panel: current.panel === PANEL_SEARCH || current.panel === PANEL_ACTIONS ? PANEL_ACTIONS : current.panel,
    activeRule: current.panel === PANEL_RULE_EDITOR ? current.activeRule : null
  };
}
export type CreateWaveGroupInlinePanelProps = {
  readonly suggestedName: string;
  readonly defaultLabel: string;
  readonly disabled?: boolean;
  readonly selectedGroup: ApiGroupFull | null;
  readonly allowGroupClear?: boolean;
  readonly collapseOnClickAway?: boolean;
  readonly startMode?: "actions" | "existing";
  readonly onChange: (group: ApiGroupFull | null) => void | Promise<void>;
  readonly onCreateGroup: (payload: ApiCreateGroup) => Promise<ApiGroupFull | null>;
};
function buildUnsavedGroupDescription(): string {
  return "Not applied yet.";
}
function useCreateWaveGroupInlinePanelViewState(t0) {
  const $ = _c(25);
  const {
    builder,
    defaultLabel,
    disabled,
    isCreating,
    selectedGroup
  } = t0;
  let t1;
  if ($[0] !== builder || $[1] !== disabled) {
    t1 = getDisplayedBuilder({
      builder,
      disabled
    });
    $[0] = builder;
    $[1] = disabled;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const displayedBuilder = t1;
  let t2;
  if ($[3] !== builder.draft || $[4] !== builder.identities.length) {
    t2 = getInlineGroupDraftSummary({
      draft: builder.draft,
      identityCount: builder.identities.length
    });
    $[3] = builder.draft;
    $[4] = builder.identities.length;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const draftSummary = t2;
  let t3;
  if ($[6] !== builder.draft) {
    t3 = validateGroupPayload(builder.draft);
    $[6] = builder.draft;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const validation = t3;
  const canCreateDraft = validation.valid && !disabled && !isCreating;
  const canResetDraft = !!draftSummary && !disabled && !isCreating;
  const isIdentityPanel = displayedBuilder.panel === PANEL_IDENTITY;
  const isRulePanel = displayedBuilder.panel === PANEL_RULE_LIST || displayedBuilder.panel === PANEL_RULE_EDITOR;
  const isSearchPanel = displayedBuilder.panel === PANEL_SEARCH;
  const hasUnsavedGroup = !!draftSummary && !disabled;
  const currentGroupLabel = selectedGroup?.name ?? defaultLabel;
  const t4 = displayedBuilder.panel !== PANEL_ACTIONS;
  const t5 = hasUnsavedGroup && !isSearchPanel;
  let t6;
  if ($[8] !== hasUnsavedGroup) {
    t6 = hasUnsavedGroup ? buildUnsavedGroupDescription() : null;
    $[8] = hasUnsavedGroup;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = hasUnsavedGroup ? draftSummary : null;
  let t8;
  if ($[10] !== canCreateDraft || $[11] !== canResetDraft || $[12] !== currentGroupLabel || $[13] !== displayedBuilder || $[14] !== draftSummary || $[15] !== hasUnsavedGroup || $[16] !== isIdentityPanel || $[17] !== isRulePanel || $[18] !== isSearchPanel || $[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== validation.valid) {
    t8 = {
      canCreateDraft,
      canResetDraft,
      currentGroupLabel,
      displayedBuilder,
      draftSummary,
      hasUnsavedGroup,
      isDraftValid: validation.valid,
      isExpandedPanel: t4,
      isIdentityPanel,
      isRulePanel,
      isSearchPanel,
      showDraftFooter: t5,
      unsavedGroupDescription: t6,
      unsavedGroupSummary: t7
    };
    $[10] = canCreateDraft;
    $[11] = canResetDraft;
    $[12] = currentGroupLabel;
    $[13] = displayedBuilder;
    $[14] = draftSummary;
    $[15] = hasUnsavedGroup;
    $[16] = isIdentityPanel;
    $[17] = isRulePanel;
    $[18] = isSearchPanel;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = validation.valid;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}
function useCreateWaveGroupInlinePanelController({
  allowGroupClear,
  builder,
  canCreateDraft,
  canResetDraft,
  onChange,
  onCreateGroup,
  setBuilder,
  setIsCreating,
  suggestedName
}: {
  readonly allowGroupClear: boolean;
  readonly builder: CreateWaveInlineGroupBuilderState;
  readonly canCreateDraft: boolean;
  readonly canResetDraft: boolean;
  readonly onChange: (group: ApiGroupFull | null) => void | Promise<void>;
  readonly onCreateGroup: (payload: ApiCreateGroup) => Promise<ApiGroupFull | null>;
  readonly setBuilder: Dispatch<SetStateAction<CreateWaveInlineGroupBuilderState>>;
  readonly setIsCreating: Dispatch<SetStateAction<boolean>>;
  readonly suggestedName: string;
}) {
  const resetBuilder = () => {
    setBuilder(createInitialInlineGroupBuilderState());
  };
  const collapseBuilderPanel = () => {
    setBuilder(collapseInlineGroupBuilder);
  };
  const setDraft = (draft: ApiCreateGroup) => {
    setBuilder(current => ({
      ...current,
      draft
    }));
  };
  const addIdentity = (identity: CommunityMemberMinimal) => {
    setBuilder(current => {
      const identities = dedupeInlineIdentities([...current.identities, identity]);
      return buildIdentityDraftState({
        current,
        identities
      });
    });
  };
  const removeIdentity = (wallet: string) => {
    const normalizedWallet = wallet.trim().toLowerCase();
    setBuilder(current => {
      const identities = current.identities.filter(identity => {
        const key = identity.wallet.trim().toLowerCase();
        return key !== normalizedWallet;
      });
      return buildIdentityDraftState({
        current,
        identities
      });
    });
  };
  const openPanel = (panel: CreateWaveInlineGroupPanel) => {
    setBuilder(current => openInlineGroupPanel({
      current,
      panel
    }));
  };
  const togglePanel = (panel: CreateWaveInlineGroupPanel, isActive: boolean) => {
    if (isActive) {
      collapseBuilderPanel();
      return;
    }
    openPanel(panel);
  };
  const openRule = (rule: CreateWaveInlineGroupRuleType) => {
    setBuilder(current => openInlineGroupRule({
      current,
      rule
    }));
  };
  const toggleRule = (rule: CreateWaveInlineGroupRuleType) => {
    setBuilder(current => toggleInlineGroupRule({
      current,
      rule
    }));
  };
  const createAndUse = async () => {
    if (!canCreateDraft) {
      return;
    }
    setIsCreating(true);
    try {
      const nextPayload: ApiCreateGroup = {
        ...builder.draft,
        name: suggestedName.trim() || "Wave Group"
      };
      const createdGroup = await onCreateGroup(nextPayload);
      if (!createdGroup) {
        return;
      }
      await onChange(createdGroup);
      resetBuilder();
    } finally {
      setIsCreating(false);
    }
  };
  const onCreateAndUse = () => {
    void createAndUse();
  };
  const onClearAll = () => {
    if (!canResetDraft) {
      return;
    }
    setBuilder(clearInlineGroupDraft);
  };
  const selectExistingGroup = async (group: ApiGroupFull | null) => {
    if (!group && !allowGroupClear) {
      return;
    }
    await onChange(group);
    if (group) {
      resetBuilder();
    }
  };
  const onExistingGroupSelect = (group: ApiGroupFull | null) => {
    void selectExistingGroup(group);
  };
  return {
    addIdentity,
    collapseBuilderPanel,
    onCancelPanel: collapseBuilderPanel,
    onClearAll,
    onCreateAndUse,
    onExistingGroupSelect,
    openRule,
    removeIdentity,
    setDraft,
    togglePanel,
    toggleRule
  };
}
export function useCreateWaveGroupInlinePanel(t0) {
  const $ = _c(46);
  const {
    suggestedName,
    defaultLabel,
    disabled: t1,
    selectedGroup,
    allowGroupClear: t2,
    collapseOnClickAway: t3,
    startMode: t4,
    onChange,
    onCreateGroup
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const allowGroupClear = t2 === undefined ? true : t2;
  const collapseOnClickAway = t3 === undefined ? true : t3;
  const startMode = t4 === undefined ? "actions" : t4;
  const panelRef = useRef(null);
  const [isCreating, setIsCreating] = useState(false);
  let t5;
  if ($[0] !== startMode) {
    t5 = () => ({
      ...createInitialInlineGroupBuilderState(),
      panel: startMode === "existing" ? PANEL_SEARCH : PANEL_ACTIONS
    });
    $[0] = startMode;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const [builder, setBuilder] = useState(t5);
  let t6;
  if ($[2] !== builder || $[3] !== defaultLabel || $[4] !== disabled || $[5] !== isCreating || $[6] !== selectedGroup) {
    t6 = {
      builder,
      defaultLabel,
      disabled,
      isCreating,
      selectedGroup
    };
    $[2] = builder;
    $[3] = defaultLabel;
    $[4] = disabled;
    $[5] = isCreating;
    $[6] = selectedGroup;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const {
    canCreateDraft,
    canResetDraft,
    currentGroupLabel,
    displayedBuilder,
    draftSummary,
    hasUnsavedGroup,
    isDraftValid,
    isExpandedPanel,
    isIdentityPanel,
    isRulePanel,
    isSearchPanel,
    showDraftFooter,
    unsavedGroupDescription,
    unsavedGroupSummary
  } = useCreateWaveGroupInlinePanelViewState(t6);
  let t7;
  if ($[8] !== allowGroupClear || $[9] !== builder || $[10] !== canCreateDraft || $[11] !== canResetDraft || $[12] !== onChange || $[13] !== onCreateGroup || $[14] !== suggestedName) {
    t7 = {
      allowGroupClear,
      builder,
      canCreateDraft,
      canResetDraft,
      onChange,
      onCreateGroup,
      setBuilder,
      setIsCreating,
      suggestedName
    };
    $[8] = allowGroupClear;
    $[9] = builder;
    $[10] = canCreateDraft;
    $[11] = canResetDraft;
    $[12] = onChange;
    $[13] = onCreateGroup;
    $[14] = suggestedName;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const {
    addIdentity,
    collapseBuilderPanel,
    onCancelPanel,
    onClearAll,
    onCreateAndUse,
    onExistingGroupSelect,
    openRule,
    removeIdentity,
    setDraft,
    togglePanel,
    toggleRule
  } = useCreateWaveGroupInlinePanelController(t7);
  let t8;
  if ($[16] !== builder || $[17] !== collapseBuilderPanel || $[18] !== collapseOnClickAway) {
    t8 = () => {
      if (collapseOnClickAway && builder.panel !== PANEL_ACTIONS) {
        collapseBuilderPanel();
      }
    };
    $[16] = builder;
    $[17] = collapseBuilderPanel;
    $[18] = collapseOnClickAway;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  useClickAway(panelRef, t8);
  let t9;
  if ($[20] !== addIdentity || $[21] !== canCreateDraft || $[22] !== canResetDraft || $[23] !== currentGroupLabel || $[24] !== displayedBuilder || $[25] !== draftSummary || $[26] !== hasUnsavedGroup || $[27] !== isCreating || $[28] !== isDraftValid || $[29] !== isExpandedPanel || $[30] !== isIdentityPanel || $[31] !== isRulePanel || $[32] !== isSearchPanel || $[33] !== onCancelPanel || $[34] !== onClearAll || $[35] !== onCreateAndUse || $[36] !== onExistingGroupSelect || $[37] !== openRule || $[38] !== removeIdentity || $[39] !== setDraft || $[40] !== showDraftFooter || $[41] !== togglePanel || $[42] !== toggleRule || $[43] !== unsavedGroupDescription || $[44] !== unsavedGroupSummary) {
    t9 = {
      addIdentity,
      canCreateDraft,
      canResetDraft,
      currentGroupLabel,
      displayedBuilder,
      draftSummary,
      hasUnsavedGroup,
      isCreating,
      isDraftValid,
      isExpandedPanel,
      isIdentityPanel,
      isRulePanel,
      isSearchPanel,
      onCancelPanel,
      onClearAll,
      onCreateAndUse,
      onExistingGroupSelect,
      openRule,
      panelRef,
      removeIdentity,
      setDraft,
      showDraftFooter,
      togglePanel,
      toggleRule,
      unsavedGroupDescription,
      unsavedGroupSummary
    };
    $[20] = addIdentity;
    $[21] = canCreateDraft;
    $[22] = canResetDraft;
    $[23] = currentGroupLabel;
    $[24] = displayedBuilder;
    $[25] = draftSummary;
    $[26] = hasUnsavedGroup;
    $[27] = isCreating;
    $[28] = isDraftValid;
    $[29] = isExpandedPanel;
    $[30] = isIdentityPanel;
    $[31] = isRulePanel;
    $[32] = isSearchPanel;
    $[33] = onCancelPanel;
    $[34] = onClearAll;
    $[35] = onCreateAndUse;
    $[36] = onExistingGroupSelect;
    $[37] = openRule;
    $[38] = removeIdentity;
    $[39] = setDraft;
    $[40] = showDraftFooter;
    $[41] = togglePanel;
    $[42] = toggleRule;
    $[43] = unsavedGroupDescription;
    $[44] = unsavedGroupSummary;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  return t9;
}